import { RefObject, useCallback, useEffect } from 'react';
import { isMobile } from '@direct-frontend/platform-code';

import { willOpenMobileKeyboard } from '../../../../lib/dom/willOpenMobileKeyboard/willOpenMobileKeyboard';

interface ViewportSize {
    height: number;
}

const visualViewport = typeof document !== 'undefined' && window.visualViewport;

function getViewportSize(): ViewportSize | undefined {
    if (!visualViewport) {
        return undefined;
    }

    return {
        height: visualViewport.height,
    };
}

/**
 * Реализация взята из пакета react-aria useViewportSize,
 * отличается использованием setTimeout вместо RAF в логике ожидания нового фокуса.
 * Работает более стабильно
 *
 * https://nda.ya.ru/t/Mmxk-D_J7LT2gf
 *
 * Подписка активируется только на мобильных устройствах (определяется через `isMobile()`).
 *
 * @param ref - ref контейнера оверлея
 */
export function useMobileViewportOverlayResize(ref: RefObject<HTMLElement>) {
    const onViewportResize = useCallback(
        (size: ViewportSize) => {
            if (ref.current) {
                // Меняем размер оверлея — это заставляет адаптироваться к появлению клавиатуры всё,
                // что внутри него отрисовано через position: absolute.
                ref.current.style.height = `${size.height}px`;
            }
        },
        [ref],
    );

    useEffect(() => {
        if (!isMobile()) {
            return undefined;
        }

        // Используем visualViewport API, чтобы отслеживать доступную высоту
        // даже при открытии виртуальной клавиатуры на iOS
        let onResize = () => {
            const newSize = getViewportSize();

            if (!newSize) {
                return;
            }

            onViewportResize({
                height: newSize.height,
            });
        };

        // При закрытии клавиатуры iOS не вызывает событие resize visual viewport
        // до завершения анимации.
        // Мы можем предугадать это и заранее изменить размер, обрабатывая событие blur и используя размер layout.
        let frame: NodeJS.Timeout;
        let onBlur = (e: FocusEvent) => {
            if (e.target instanceof Element && willOpenMobileKeyboard(e.target)) {
                // Ждём 10мс, чтобы проверить, получил ли фокус новый элемент с клавиатурой.
                frame = setTimeout(() => {
                    if (!document.activeElement || !willOpenMobileKeyboard(document.activeElement)) {
                        onViewportResize({
                            height: window.innerHeight,
                        });
                    }
                }, 10);
            }
        };

        window.addEventListener('blur', onBlur, true);

        if (visualViewport) {
            visualViewport.addEventListener('resize', onResize);
        }

        return () => {
            clearTimeout(frame);
            window.removeEventListener('blur', onBlur, true);
            if (visualViewport) {
                visualViewport.removeEventListener('resize', onResize);
            }
        };
    }, [onViewportResize]);
}
